import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { Location as LocationType, Provider as ProviderType } from 'app/generated/graphql'
import {
  CREATE_COMM_LOG_WRITEBACK_RULE,
  DELETE_COMM_LOG_WRITEBACK_RULE,
  UPDATE_COMM_LOG_WRITEBACK_RULE,
} from 'app/components/pms-comm-log/graphql'
import { gqlFetchList, gqlMutation } from 'app/lib/graphqlClient'
import { effectiveChannels, planRuleChanges, type PmsCommLogChannel, type PmsCommLogMap } from 'app/lib/pmsCommLog'
import type { PmsCommLogSavedRule } from 'app/types/pms-comm-log'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useLocationStore, useProviderStore } from 'app/stores/StoreContext'
import { pmsDisplayName } from 'lib/pmsNames'

type CommLogSettings = {
  commLogWritebackEnabled?: boolean
  commLogWritebackProvider?: string
}

export type PmsCommLogProviderOption = { value: string; label: string }

export type UsePmsCommLogReturn = {
  location: LocationType | undefined
  isKollaLocation: boolean
  pmsName: string
  enabled: boolean
  toggleEnabled: (next: boolean) => void
  channels: PmsCommLogMap
  setChannel: (key: string, channel: PmsCommLogChannel | undefined) => void
  providerOptions: PmsCommLogProviderOption[]
  provider: string | undefined
  setProvider: (resourceName: string) => void
  isDirty: boolean
  saveError: string | undefined
  onSubmit: () => Promise<void>
}

export function usePmsCommLog(): UsePmsCommLogReturn {
  const { locationId } = useParams()
  const store = useLocationStore()
  const providerStore = useProviderStore()
  const [providers, setProviders] = useState<ProviderType[]>([])
  const [provider, setProvider] = useState<string | undefined>()
  const [location, setLocation] = useState<LocationType | undefined>()
  const [enabled, setEnabled] = useState(false)
  const [channels, setChannels] = useState<PmsCommLogMap>({})
  const [savedRules, setSavedRules] = useState<PmsCommLogSavedRule[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const [saveError, setSaveError] = useState<string | undefined>()

  const fetchRules = async (id: string) =>
    (await gqlFetchList(`commLogWritebackRules`, { search: `locationId:${id}`, pagination: { limit: 100 } })).hits

  useEffect(() => {
    if (!locationId) return
    Promise.all([store.fetchOne(locationId), fetchRules(locationId)]).then(([, rules]) => {
      const loc = store.current as LocationType | undefined
      if (!loc) return
      const settings = loc.settings as CommLogSettings | undefined
      setLocation(loc)
      setEnabled(settings?.commLogWritebackEnabled ?? false)
      setSavedRules(rules)
      setChannels(effectiveChannels(rules))
      setProvider(settings?.commLogWritebackProvider)
      setIsDirty(false)
    })
    providerStore
      .fetchList({ search: `kollaConsumerId:${locationId}`, pagination: { limit: 200 } })
      .then(() => setProviders(providerStore.list?.hits ?? []))
  }, [locationId])

  const chooseProvider = useCallback((resourceName: string) => {
    setProvider(resourceName)
    setIsDirty(true)
  }, [])

  const toggleEnabled = useCallback((next: boolean) => {
    setEnabled(next)
    setIsDirty(true)
  }, [])

  const setChannel = useCallback((key: string, channel: PmsCommLogChannel | undefined) => {
    setChannels((prev) => {
      const next = { ...prev }
      if (channel) {
        next[key] = channel
      } else {
        delete next[key]
      }
      return next
    })
    setIsDirty(true)
  }, [])

  const onSubmit = useCallback(async () => {
    if (!locationId || !location) return
    setSaveError(undefined)
    if (enabled && !provider) {
      setSaveError(`Choose who the entries are filed under in the PMS.`)
      return
    }
    // updateLocation merges settings since trackable-api#1207, so sending only these keys preserves the rest.
    const result = await store.update(locationId, {
      settings: { commLogWritebackEnabled: enabled, commLogWritebackProvider: provider },
    })
    const plan = planRuleChanges(savedRules, channels)
    const writes = await Promise.all([
      ...plan.create.map((rule) =>
        gqlMutation(CREATE_COMM_LOG_WRITEBACK_RULE, { input: { locationId, type: rule.type, channel: rule.channel } }),
      ),
      ...plan.update.map((rule) =>
        gqlMutation(UPDATE_COMM_LOG_WRITEBACK_RULE, { id: rule.id, input: { channel: rule.channel } }),
      ),
      ...plan.remove.map((id) => gqlMutation(DELETE_COMM_LOG_WRITEBACK_RULE, { id })),
    ])
    const error = result.error ?? writes.find((write) => write.error)?.error
    const rules = await fetchRules(locationId)
    setSavedRules(rules)
    if (error) {
      setChannels(effectiveChannels(rules))
      setSaveError(error.message)
      notifyError(`Could not save comm log settings`)
      return
    }
    setLocation(store.current as LocationType | undefined)
    setIsDirty(false)
    notifySuccess(`Comm log settings saved`)
  }, [locationId, location, enabled, channels, provider, savedRules])

  return {
    location,
    isKollaLocation: !!location?.kollaConnectorId,
    pmsName: pmsDisplayName(location?.pms, `your PMS`),
    enabled,
    toggleEnabled,
    channels,
    setChannel,
    providerOptions: providers
      .filter((p) => !p.deactivated || p.resourceName === provider)
      .map((p) => ({ value: p.resourceName, label: p.pmsDisplayName || p.displayName })),
    provider,
    setProvider: chooseProvider,
    isDirty,
    saveError,
    onSubmit,
  }
}
